<template>
  <page>
    <v-card flat>
      <v-toolbar dense color="grey lighten-3" flat>
        <v-icon v-if="newPolTypeScheme">mdi-plus</v-icon>
        <v-icon v-else>mdi-pencil</v-icon>
        <v-toolbar-title v-if="newPolTypeScheme">Create New Permitted Scheme per Policy Type</v-toolbar-title>
        <v-toolbar-title v-else-if="isQuery">Query Permitted Scheme per Policy Type</v-toolbar-title>
        <v-toolbar-title v-else>Edit Permitted Scheme per Policy Type</v-toolbar-title>
      </v-toolbar>
      <v-divider></v-divider>
      <v-flex xs12>
        <message :messages="messages"></message>
      </v-flex>
      <v-card-text>
        <v-container fluid grid-list-lg>
          <v-layout row wrap>
            <v-flex xs12 v-if="newPolTypeScheme">
              <v-autocomplete
                :items="schemeList"
                v-model="polTypeSchemeForm.scheme"
                :label="labels.scheme"
                item-text="label"
                item-value="value"
                data-vv-name="scheme"
                :data-vv-as="labels.scheme"
                :error-messages="errors.collect('scheme')"
                v-validate="'required'"
              ></v-autocomplete>
            </v-flex>
            <v-flex xs12 v-else>
              <v-subheader class="queryLabel">{{ labels.scheme }}</v-subheader>
              <div class="queryValue">{{ getDesc('scheme', polTypeSchemeForm.scheme) }}</div>
            </v-flex>
          </v-layout>
          <v-layout row wrap>
            <v-flex xs12 v-if="newPolTypeScheme">
              <v-autocomplete
                :items="polTypeList"
                v-model="polTypeSchemeForm.polType"
                :label="labels.polType"
                item-text="label"
                item-value="value"
                data-vv-name="polType"
                :data-vv-as="labels.polType"
                :error-messages="errors.collect('polType')"
                v-validate="'required'"
              ></v-autocomplete>
            </v-flex>
            <v-flex xs12 v-else>
              <v-subheader class="queryLabel">{{ labels.polType }}</v-subheader>
              <div class="queryValue">{{ getDesc('polType', polTypeSchemeForm.polType) }}</div>
            </v-flex>
          </v-layout>
          <v-layout row wrap>
            <v-flex xs6 v-if="newPolTypeScheme">
              <cal-picker
                :label="labels.effDate"
                v-model="polTypeSchemeForm.effDate"
                :error-messages="errors.collect('effDate')"
                data-vv-name="effDate"
                :max="polTypeSchemeForm.expDate"
                :data-vv-as="labels.effDate"
                v-validate="'required'"
              ></cal-picker>
            </v-flex>
            <v-flex xs6 v-else>
              <v-subheader class="queryLabel">{{ labels.effDate }}</v-subheader>
              <div class="queryValue">{{ formatDisplayDate(polTypeSchemeForm.effDate) }}</div>
            </v-flex>
            <v-flex xs6 v-if="isQuery">
              <cal-picker
                :label="labels.expDate"
                v-model="polTypeSchemeForm.expDate"
                :error-messages="errors.collect('expDate')"
                data-vv-name="expDate"
                :min="minExpDate(polTypeSchemeForm.effDate)"
                :data-vv-as="labels.expDate"
                v-validate="validateExpDate(polTypeSchemeForm.effDate)"
                disabled
              ></cal-picker>
            </v-flex>
            <v-flex xs6 v-else>
              <cal-picker
                :label="labels.expDate"
                v-model="polTypeSchemeForm.expDate"
                :error-messages="errors.collect('expDate')"
                data-vv-name="expDate"
                :min="minExpDate(polTypeSchemeForm.effDate)"
                :data-vv-as="labels.expDate"
                v-validate="validateExpDate(polTypeSchemeForm.effDate)"
              ></cal-picker>
            </v-flex>
          </v-layout>
          <v-layout row wrap>
            <v-flex xs4 v-if="isQuery">
              <v-autocomplete
                :items="commGnList"
                v-model="polTypeSchemeForm.commGn"
                :label="labels.commGn"
                item-text="label"
                item-value="value"
                data-vv-name="commGn"
                :data-vv-as="labels.commGn"
                :error-messages="errors.collect('commGn')"
                disabled
              ></v-autocomplete>
            </v-flex>
            <v-flex xs4 v-else>
              <v-autocomplete
                :items="commGnList"
                v-model="polTypeSchemeForm.commGn"
                :label="labels.commGn"
                item-text="label"
                item-value="value"
                data-vv-name="commGn"
                :data-vv-as="labels.commGn"
                :error-messages="errors.collect('commGn')"
              ></v-autocomplete>
            </v-flex>
            <v-flex xs4 v-if="isQuery">
              <v-text-field
                v-model="polTypeSchemeForm.commPct"
                v-num-mask="{ negative: false, intScale: 3, decScale: 3 }"
                :label="labels.commPct"
                :error-messages="errors.collect('commPct')"
                data-vv-name="commPct"
                append-icon="mdi-percent"
                :data-vv-as="labels.commPct"
                v-validate="'mask_max_value:100'"
                disabled
              ></v-text-field>
            </v-flex>
            <v-flex xs4 v-else>
              <v-text-field
                v-model="polTypeSchemeForm.commPct"
                v-num-mask="{ negative: false, intScale: 3, decScale: 3 }"
                :label="labels.commPct"
                :error-messages="errors.collect('commPct')"
                data-vv-name="commPct"
                append-icon="mdi-percent"
                :data-vv-as="labels.commPct"
                v-validate="'mask_max_value:100'"
              ></v-text-field>
            </v-flex>
            <v-flex xs4 v-if="isQuery">
              <v-checkbox
                v-model="polTypeSchemeForm.protComm"
                :label="labels.protComm"
                :error-messages="errors.collect('protComm')"
                data-vv-name="protComm"
                :data-vv-as="labels.protComm"
                hide-details
                disabled
              ></v-checkbox>
            </v-flex>
            <v-flex xs4 v-else>
              <v-checkbox
                v-model="polTypeSchemeForm.protComm"
                :label="labels.protComm"
                :error-messages="errors.collect('protComm')"
                data-vv-name="protComm"
                :data-vv-as="labels.protComm"
                hide-details
              ></v-checkbox>
            </v-flex>
          </v-layout>
          <v-layout row wrap>
            <v-flex xs4 v-if="isQuery">
              <v-text-field
                v-model="polTypeSchemeForm.discPct"
                v-num-mask="{ negative: false, intScale: 3, decScale: 3 }"
                :label="labels.discPct"
                :error-messages="errors.collect('discPct')"
                data-vv-name="discPct"
                append-icon="mdi-percent"
                :data-vv-as="labels.discPct"
                v-validate="'mask_max_value:100'"
                disabled
              ></v-text-field>
            </v-flex>
            <v-flex xs4 v-else>
              <v-text-field
                v-model="polTypeSchemeForm.discPct"
                v-num-mask="{ negative: false, intScale: 3, decScale: 3 }"
                :label="labels.discPct"
                :error-messages="errors.collect('discPct')"
                data-vv-name="discPct"
                append-icon="mdi-percent"
                :data-vv-as="labels.discPct"
                v-validate="'mask_max_value:100'"
              ></v-text-field>
            </v-flex>
            <v-flex xs4></v-flex>
            <v-flex xs4 v-if="isQuery">
              <v-checkbox
                v-model="polTypeSchemeForm.protDisc"
                :label="labels.protDisc"
                :error-messages="errors.collect('protDisc')"
                data-vv-name="protDisc"
                :data-vv-as="labels.protDisc"
                hide-details
                disabled
              ></v-checkbox>
            </v-flex>
            <v-flex xs4 v-else>
              <v-checkbox
                v-model="polTypeSchemeForm.protDisc"
                :label="labels.protDisc"
                :error-messages="errors.collect('protDisc')"
                data-vv-name="protDisc"
                :data-vv-as="labels.protDisc"
                hide-details
              ></v-checkbox>
            </v-flex>
          </v-layout>
          <v-layout row wrap>
            <v-flex xs12 v-if="isQuery">
              <v-autocomplete
                :items="filteredPromotionList"
                v-model="polTypeSchemeForm.promotion"
                :label="labels.promotion"
                item-text="label"
                item-value="value"
                data-vv-name="promotion"
                :data-vv-as="labels.promotion"
                :error-messages="errors.collect('promotion')"
                clearable
                disabled
              ></v-autocomplete>
            </v-flex>
            <v-flex xs12 v-else>
              <v-autocomplete
                :items="filteredPromotionList"
                v-model="polTypeSchemeForm.promotion"
                :label="labels.promotion"
                item-text="label"
                item-value="value"
                data-vv-name="promotion"
                :data-vv-as="labels.promotion"
                :error-messages="errors.collect('promotion')"
                clearable
              ></v-autocomplete>
            </v-flex>
          </v-layout>
          <v-layout row wrap>
            <v-flex xs6>
              <v-subheader class="queryLabel">{{ labels.createdBy }}</v-subheader>
              <div class="queryValue">{{ polTypeSchemeForm.createdName }}</div>
            </v-flex>
            <v-flex xs6>
              <v-subheader class="queryLabel">{{ labels.createdDate }}</v-subheader>
              <div class="queryValue">{{ formatDisplayDate(polTypeSchemeForm.createdDate) }}</div>
            </v-flex>
          </v-layout>
          <v-layout row wrap v-if="!newPolTypeScheme">
            <v-flex xs6>
              <v-subheader class="queryLabel">{{ labels.modifiedBy }}</v-subheader>
              <div class="queryValue">{{ polTypeSchemeForm.modifiedName }}</div>
            </v-flex>
            <v-flex xs6>
              <v-subheader class="queryLabel">{{ labels.modifiedDate }}</v-subheader>
              <div class="queryValue">{{ formatDisplayDate(polTypeSchemeForm.modifiedDate) }}</div>
            </v-flex>
          </v-layout>
        </v-container>
        <v-layout>
          <v-btn class="mr-2" color="error" @click="doCancel" :loading="submitting">
            <span>Cancel</span>
            <v-icon right dark>mdi-cancel</v-icon>
          </v-btn>
          <v-flex lg6 md12 xs12 v-if="!isQuery">
            <v-btn color="primary" type="submit" :loading="submitting" @click.prevent="doSave">
              {{ newPolTypeScheme ? 'Save Policy Type' : 'Save Policy Type' }}
              <v-icon right>{{ newPolTypeScheme ? 'mdi-content-save' : 'mdi-content-save' }}</v-icon>
            </v-btn>
          </v-flex>
        </v-layout>
      </v-card-text>
    </v-card>
  </page>
</template>

<script>
import { expandURI } from '@/utils/ajax'
import MessageMixin from '@/mixins/message'
import { formatDisplayDate, removeTime } from '@/utils/date'
import { parseISO } from 'date-fns'

export default {
  data() {
    return {
      toogleArray: [true, false],
      labels: {
        scheme: 'Scheme Code',
        polType: 'Policy Type',
        effDate: 'Effective Date',
        expDate: 'Expiry Date',
        commGn: 'Commission G/N',
        commPct: 'Commission Percent',
        protComm: 'Protect Commission',
        discPct: 'Discount Percent',
        protDisc: 'Protect Discount',
        promotion: 'Promotion',
        createdBy: 'Created By',
        createdDate: 'Created Date',
        modifiedBy: 'Modified By',
        modifiedDate: 'Modified Date'
      },
      polTypeSchemeForm: {
        scheme: null,
        polType: null,
        effDate: undefined,
        expDate: undefined,
        commGn: null,
        commPct: parseFloat(0).toFixed(3),
        protComm: false,
        discPct: parseFloat(0).toFixed(3),
        protDisc: false,
        promotion: null,
        createdName: this.$store.state.token.firstName + ' ' + this.$store.state.token.lastName,
        createdDate: new Date(),
        modifiedName: null,
        modifiedDate: null
      },
      today: new Date(),
      schemeList: [],
      polTypeList: [],
      promotionList: [],
      filteredPromotionList: [],
      newPolTypeScheme: false,
      submitting: false,
      loading: true,
      pagination: {
        sortDesc: [false],
        page: 1
      }
    }
  },
  watch: {
    'polTypeSchemeForm.effDate': function (value) {
      this.getFilteredPromotionList()
    },
    'polTypeSchemeForm.expDate': function (value) {
      this.getFilteredPromotionList()
    }
  },
  computed: {
    menu() {
      return this.$store.state.pkgParam.polTypeScheme.menu
    },
    scheme() {
      return this.$route.params.scheme
    },
    polType() {
      return this.$route.params.polType
    },
    isQuery() {
      return this.$route.params.isQuery
    },
    commGnList() {
      return [
        { label: 'G - Gross', value: 'G' },
        { label: 'N - Net', value: 'N' }
      ]
    }
  },
  methods: {
    getDesc(field, value) {
      var list = []
      if (field === 'scheme') {
        list = this.schemeList
      } else if (field === 'polType') {
        list = this.polTypeList
      }
      for (var i in list) {
        if (list[i].value === value) {
          return list[i].label
        }
      }
    },
    minExpDate(xDate) {
      if (this.newPolTypeScheme) {
        return this.polTypeSchemeForm.effDate
      } else {
        if (xDate) {
          return parseISO(xDate)
        }
      }
    },
    formatDisplayDate(xDate) {
      if (xDate === null) {
        return undefined
      } else {
        return formatDisplayDate(xDate)
      }
    },
    validateExpDate(xDate) {
      if (xDate) {
        return 'checkMinDate:' + xDate
      } else {
        return 'checkMinDate'
      }
    },
    async fetchData() {
      if (this.scheme === 'new') {
        this.newPolTypeScheme = true
      } else {
        const apiURI = this.menu.getPolTypeScheme
        if (apiURI) {
          try {
            this.loading = true
            const expand = {
              scheme: this.scheme,
              polType: this.polType
            }
            const result = await this.$http.get(expandURI(apiURI, expand))
            const { data } = result.data
            if (data) {
              this.polTypeSchemeForm.scheme = data.polTypeScheme.scheme
              this.polTypeSchemeForm.polType = data.polTypeScheme.polType
              this.polTypeSchemeForm.effDate = data.polTypeScheme.effDate
              this.polTypeSchemeForm.expDate = data.polTypeScheme.expDate
              this.polTypeSchemeForm.commGn = data.polTypeScheme.commGn
              this.polTypeSchemeForm.commPct = data.polTypeScheme.commPct
              if (this.polTypeSchemeForm.commPct === null) {
                this.polTypeSchemeForm.commPct = parseFloat(0).toFixed(3)
              }
              this.polTypeSchemeForm.protComm = data.polTypeScheme.protComm
              this.polTypeSchemeForm.discPct = data.polTypeScheme.discPct
              if (this.polTypeSchemeForm.discPct === null) {
                this.polTypeSchemeForm.discPct = parseFloat(0).toFixed(3)
              }
              this.polTypeSchemeForm.protDisc = data.polTypeScheme.protDisc
              this.polTypeSchemeForm.promotion = data.polTypeScheme.promotion
              this.polTypeSchemeForm.createdName = data.polTypeScheme.createdName
              this.polTypeSchemeForm.createdDate = data.polTypeScheme.createdDate
              this.polTypeSchemeForm.modifiedName = data.polTypeScheme.modifiedName
              this.polTypeSchemeForm.modifiedDate = data.polTypeScheme.modifiedDate

              this.getFilteredPromotionList()
            }
            this.loading = false
          } catch (err) {
            this.loading = false
            const { data } = err.response
            if (data) {
              this.message(data)
              this.fieldMessage(data)
            }
          }
        }
      }
    },
    async getSchemeList() {
      const apiURI = this.menu.getSchemeList
      if (apiURI) {
        const result = await this.$http.get(apiURI)
        const { data } = result.data
        this.schemeList = []
        if (data) {
          this.schemeList = data.schemeList
        }
      }
    },
    async getPolTypeList() {
      const apiURI = this.menu.getPolTypeList
      if (apiURI) {
        const result = await this.$http.get(apiURI)
        const { data } = result.data
        this.polTypeList = []
        if (data) {
          this.polTypeList = data.polTypeList
        }
      }
    },
    async getPromotionList() {
      const apiURI = this.menu.getPromotionList
      if (apiURI) {
        const result = await this.$http.get(apiURI)
        const { data } = result.data
        this.promotionList = []
        if (data) {
          this.promotionList = data.promotionList
        }
      }
    },
    getFilteredPromotionList() {
      this.filteredPromotionList = []
      var effDate = removeTime(this.polTypeSchemeForm.effDate)
      var expDate = removeTime(this.polTypeSchemeForm.expDate)
      for (var i in this.promotionList) {
        var promoEffDate = removeTime(this.promotionList[i].secondaryValue)
        var promoExpDate = removeTime(this.promotionList[i].thirdValue)
        if (this.polTypeSchemeForm.effDate !== undefined && this.polTypeSchemeForm.expDate !== undefined) {
          if (effDate >= promoEffDate && expDate <= promoExpDate) {
            this.filteredPromotionList.push(this.promotionList[i])
          }
        } else if (this.polTypeSchemeForm.effDate !== undefined) {
          if (effDate >= promoEffDate) {
            this.filteredPromotionList.push(this.promotionList[i])
          }
        } else if (expDate !== undefined) {
          if (this.polTypeSchemeForm.expDate <= promoExpDate) {
            this.filteredPromotionList.push(this.promotionList[i])
          }
        }
      }
    },
    doSave() {
      this.resetMessages()
      this.$validator.validateAll().then((result) => {
        if (result) {
          if (this.newPolTypeScheme) {
            this.doCreate()
          } else {
            this.doUpdate()
          }
        } else {
          this.promptFieldCheck()
        }
      })
    },
    async doUpdate() {
      const apiURI = this.menu.updatePolTypeScheme
      if (apiURI) {
        this.submitting = true
        try {
          const result = await this.$http.put(apiURI, this.polTypeSchemeForm)
          const { data } = result
          if (data) {
            this.notify(data)
            this.$router.push({ name: 'polTypeScheme' })
          }
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
        }
      }
      this.submitting = false
    },
    async doCreate() {
      const apiURI = this.menu.createPolTypeScheme
      if (apiURI) {
        this.submitting = true
        try {
          const result = await this.$http.post(apiURI, this.polTypeSchemeForm)
          const { data } = result
          if (data) {
            this.notify(data)
            this.$router.push({ name: 'polTypeScheme' })
          }
          this.submitting = false
        } catch (err) {
          this.submitting = false
          const { data } = err.response
          if (data) {
            this.errorMessage('Record already exists')
            this.fieldMessage(data)
          }
        }
      }
    },
    doCancel() {
      this.$router.push({ name: 'polTypeScheme' })
    }
  },
  async created() {
    if (this.$store.state.pkgParam.polTypeScheme.init !== 1) {
      await this.$store.dispatch('polTypeSchemeInit')
    }
    this.fetchData()
    this.getSchemeList()
    this.getPolTypeList()
    this.getPromotionList()

    const checkMinDate = {
      getMessage: (field, args) => {
        if (field === 'Expiry Date') {
          return 'Must be a later Effective Date'
        }
        return field + ' cannot be a past date'
      },
      validate: (value, args) => {
        if (value) {
          if (args.length > 0) {
            return removeTime(value) >= removeTime(args)
          } else {
            return removeTime(value) >= removeTime(new Date())
          }
        } else {
          return true
        }
      }
    }
    this.$validator.extend('checkMinDate', checkMinDate)
  },
  mixins: [MessageMixin]
}
</script>

<style lang="scss">
</style>
